/* Verifying what a model said about an address, without believing any of it.
 *
 * Operating states were kept away from the LLM deliberately, and the reason was
 * good: you cannot disprove a hallucinated state by quote-matching, because the
 * two-letter token really is somewhere on the page. Grounding could reject a
 * fabricated VERTICAL, because the vertical's evidence quote either appears in
 * the source or it does not. It could never reject a fabricated STATE.
 *
 * So the contract changed rather than the trust. /api/analyze/locations returns
 * whole postal addresses copied out of the text, each with a ROLE — premises,
 * mail drop, carrier, agent, customer testimonial. This file checks, with no
 * judgement of its own:
 *
 *   1. the address string is really in the site text          (not invented)
 *   2. its ZIP belongs to the state it claims                 (not mismatched)
 *   3. the role is one that evidences the INSURED's operations
 *
 * A fabricated address fails 1. A plausible-looking one assembled from two
 * different places fails 2. Neither check consults the model's opinion.
 *
 * What is left for the model is the one judgement that structure and proximity
 * cannot make: that "4.8 stars — Bob A. at home in Waxahachie, TX 75165" is
 * evidence about Bob. That sentence has a city, a comma, a state code and a
 * ZIP, in the right order, in a postal address. Every deterministic signal says
 * location. Only meaning says otherwise — and now meaning cannot smuggle a
 * state in on its own, because whatever role the model picks, the address still
 * has to survive checks 1 and 2.
 */
(function (root) {

  // -----------------------------------------------------------------------
  // ZIP → state. Three-digit prefix ranges, which is the level at which the
  // USPS allocation is actually stable.
  //
  // A gap here rejects a genuine address, which costs a signal. A wrong entry
  // accepts a mismatched one, which costs a wrong state on a report. The table
  // is therefore allowed to be incomplete and is not allowed to be wrong: an
  // unknown prefix returns null and the caller treats that as "unverified",
  // never as "verified".
  // -----------------------------------------------------------------------
  const ZIP_RANGES = [
    ['AL',350,369], ['AK',995,999], ['AZ',850,865], ['AR',716,729],
    ['CA',900,961], ['CO',800,816], ['CT',60,69],   ['DE',197,199],
    ['DC',200,200], ['DC',202,205], ['FL',320,349], ['GA',300,319], ['GA',398,399],
    ['HI',967,968], ['ID',832,838], ['IL',600,629], ['IN',460,479],
    ['IA',500,528], ['KS',660,679], ['KY',400,427], ['LA',700,714],
    ['ME',39,49],   ['MD',206,219], ['MA',10,27],   ['MI',480,499],
    ['MN',550,567], ['MS',386,397], ['MO',630,658], ['MT',590,599],
    ['NE',680,693], ['NV',889,898], ['NH',30,38],   ['NJ',70,89],
    ['NM',870,884], ['NY',100,149], ['NY',5,5],     ['NC',270,289],
    ['ND',580,588], ['OH',430,459], ['OK',730,749], ['OR',970,979],
    ['PA',150,196], ['PR',6,9],     ['RI',28,29],   ['SC',290,299],
    ['SD',570,577], ['TN',370,385], ['TX',750,799], ['TX',885,885],
    ['UT',840,847], ['VT',50,59],   ['VA',201,246], ['WA',980,994],
    ['WV',247,268], ['WI',530,549], ['WY',820,831],
  ];

  /** The state a ZIP belongs to, or null where the table cannot say. */
  function zipState(zip){
    const m = String(zip || '').match(/\b(\d{5})(?:-\d{4})?\b/);
    if (!m) return null;
    const p = parseInt(m[1].slice(0, 3), 10);
    for (const [abbr, lo, hi] of ZIP_RANGES){ if (p >= lo && p <= hi) return abbr; }
    return null;
  }

  // Roles that evidence the INSURED operating somewhere. Everything else is an
  // address that is real, correctly transcribed, and about somebody else.
  const OPERATING_ROLES = new Set(['operating_site']);

  const _norm = s => String(s || '')
    .toLowerCase()
    .replace(/[.,#]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  /** Is `needle` present in any of `sources`, allowing for whitespace and
   *  punctuation differences only? Returns the source label, or null.
   *
   *  Deliberately strict about CONTENT and lax about FORMAT: a model that
   *  reflowed a line break has still copied the address, while one that
   *  corrected a street number has invented one. */
  function _presentIn(needle, sources){
    const q = _norm(needle);
    if (q.length < 8) return null;              // too short to be an address
    for (const src of (sources || [])){
      if (_norm(src.text).includes(q)) return src.label || 'site';
    }
    return null;
  }

  /**
   * Split the model's locations into what survived verification and what did
   * not, with a reason for every rejection.
   *
   * Returns { accepted, rejected }. `accepted` entries carry the source label
   * that the address was found in, so a report can cite it.
   */
  function verifyLocations(locations, sources){
    const accepted = [], rejected = [];
    const siteSrcs = (sources || []).filter(s => s.kind === 'site');
    const pool = siteSrcs.length ? siteSrcs : (sources || []);

    (locations || []).forEach(loc => {
      const rec = Object.assign({}, loc);
      const abbr = String(loc.abbr || '').toUpperCase();
      const role = String(loc.role || '').toLowerCase();

      // The role gate runs FIRST and is reported as its own reason, because
      // "the model called this a testimonial" is a finding worth showing —
      // it is the gate that the deterministic reader could never apply.
      if (!OPERATING_ROLES.has(role)){
        rejected.push(Object.assign(rec, { reason: 'role:' + (role || 'unset') }));
        return;
      }
      if (!/^[A-Z]{2}$/.test(abbr)){
        rejected.push(Object.assign(rec, { reason: 'no-state' }));
        return;
      }
      const src = _presentIn(loc.address, pool);
      if (!src){
        // The address is not in the text. Whatever it describes, we did not
        // read it — which is exactly the failure quote-matching was for.
        rejected.push(Object.assign(rec, { reason: 'not-in-text' }));
        return;
      }
      const zs = zipState(loc.zip || loc.address);
      if (zs && zs !== abbr){
        rejected.push(Object.assign(rec, { reason: 'zip-mismatch:' + zs }));
        return;
      }
      accepted.push(Object.assign(rec, {
        abbr, source: src,
        // Verified means: found in the text, and the ZIP agrees. A record with
        // no ZIP is accepted on the first check alone, and says so, rather
        // than claiming a confirmation it did not get.
        zipVerified: !!zs,
      }));
    });
    return { accepted, rejected };
  }

  /**
   * Fold verified addresses into the states the deterministic reader found.
   *
   * A state the reader already has gains the address as better evidence. A
   * state only the addresses show is added — this is the case the reader
   * structurally cannot reach, a site that gives its footprint only as street
   * addresses whose city is not in the gazetteer.
   *
   * Confidence is recomputed across the merged list, so adding a state cannot
   * leave the old percentages describing a different set.
   */
  function mergeLocationStates(states, accepted, stateNames){
    const out = (states || []).map(s => Object.assign({}, s));
    const byAbbr = new Map(out.map(s => [s.abbr, s]));
    const counts = new Map();
    (accepted || []).forEach(a => counts.set(a.abbr, (counts.get(a.abbr) || 0) + 1));

    counts.forEach((n, abbr) => {
      const first = (accepted || []).find(a => a.abbr === abbr);
      // A verified postal address is the strongest location evidence there is,
      // so it sets a floor under the score rather than adding to one.
      const floor = 2.1 * n;
      const hit = byAbbr.get(abbr);
      if (hit){
        hit.score = Math.max(hit.score || 0, floor);
        hit.siteLoc = Math.max(hit.siteLoc || 0, floor);
        hit.addresses = n;
        hit.addressEvidence = first.address;
        return;
      }
      const rec = {
        abbr,
        name: (stateNames && stateNames[abbr]) || abbr,
        score: floor, siteScore: floor, policyScore: 0,
        onSite: true, onPolicy: false,
        siteLoc: floor, policyLoc: 0,
        siteMentions: n, policyMentions: 0,
        evidence: first.address, source: first.source, matched: first.address,
        siteEvidence: first.address, policyEvidence: '',
        xcheck: 'not-on-policy',
        addresses: n, addressEvidence: first.address,
      };
      out.push(rec);
      byAbbr.set(abbr, rec);
    });

    const max = out.reduce((m, s) => Math.max(m, s.score || 0), 0);
    out.forEach(s => { s.confidence = max ? Math.round(((s.score || 0) / max) * 100) : 0; });
    out.sort((a, b) => (b.score || 0) - (a.score || 0));
    return oneRecordPerJurisdiction(out).slice(0, 4);
  }

  // -----------------------------------------------------------------------
  // WASHINGTON, DC = DISTRICT OF COLUMBIA. One jurisdiction, code DC.
  //
  // Friendship Public Charter School was reported as operating in the District
  // of Columbia AND Washington State: "Washington" in "1400 First St NW,
  // Washington, DC 20001" is the city, and every reader that matched the state
  // name "Washington" took it for WA. In an address Washington is the city
  // and DC the jurisdiction; the city name never creates a jurisdiction.
  //
  // ZIP prefixes 200 and 202–205 are DC. 201 is northern Virginia, which the
  // table above used to give to DC.
  // -----------------------------------------------------------------------
  const DC_DISPLAY = 'District of Columbia (DC)';

  /** 'DC' for any spelling of the District, else null. Ignores case, periods,
   *  commas, parentheses and extra spaces. A bare ZIP in the DC ranges counts. */
  function jurisdictionCode(name){
    const raw = String(name || '').trim();
    const z = /^(\d{5})(?:-\d{4})?$/.exec(raw);
    if (z) return zipState(z[1]) === 'DC' ? 'DC' : null;
    const t = raw.toLowerCase().replace(/[.,()]/g, ' ').replace(/\s+/g, ' ').trim();
    return /^(?:district of columbia(?: d ?c)?|(?:washington|wash)(?: district of columbia| d ?c)|d ?c)$/.test(t) ? 'DC' : null;
  }

  /** "Washington" here is DC's city when what follows it is D.C./DC, "District
   *  of Columbia", or a DC ZIP. `after` is the text right after the word. */
  function isDCCityAfter(after){
    const a = String(after || '');
    if (/^\s*,?\s*(?:D\.?\s?C\b\.?|District\s+of\s+Columbia\b)/i.test(a)) return true;
    const z = /^\s*,?\s*(\d{5})\b/.exec(a);
    return !!z && zipState(z[1]) === 'DC';
  }

  /** Every DC spelling in free text, rewritten to "District of Columbia" so a
   *  name scan cannot read the city as Washington State. For readers that keep
   *  no evidence; the operating-state scorer skips the city in place instead. */
  function canonDCText(text){
    return String(text || '')
      .replace(/\b(?:Wash(?:ington)?\.?)\s*,?\s*(?:D\.?\s?C\b\.?|District\s+of\s+Columbia\b)(?:\s*\(\s*D\.?\s?C\.?\s*\))?/gi, ' District of Columbia ')
      .replace(/\bWashington(?=\s*,?\s*(?:200|20[2-5])\d{2}\b)/gi, ' District of Columbia ');
  }

  /** ONE RECORD PER JURISDICTION — the hard stop before a report.
   *
   * Takes codes or { abbr, ... } records, maps each to its jurisdiction, and
   * merges duplicates into the first. A merged record keeps every source line
   * in `evidenceAll` ({ source, evidence }), so nothing is lost. Codes in,
   * codes out; records in, records out. Order is preserved. */
  function oneRecordPerJurisdiction(list){
    const out = [], at = new Map();
    (list || []).forEach(item => {
      const isRec = item && typeof item === 'object';
      const raw = isRec ? item.abbr : item;
      const code = jurisdictionCode(raw) || String(raw || '').toUpperCase().trim();
      if (!code) return;
      const line = isRec && (item.evidence || item.address)
        ? { source: item.source || '', evidence: item.evidence || item.address } : null;
      if (!at.has(code)){
        const rec = isRec ? Object.assign({}, item, { abbr: code }) : code;
        if (isRec){
          if (code === 'DC') rec.name = 'District of Columbia';
          rec.evidenceAll = (item.evidenceAll || []).slice();
          if (line && !rec.evidenceAll.some(e => e.evidence === line.evidence)) rec.evidenceAll.push(line);
        }
        at.set(code, out.length); out.push(rec);
        return;
      }
      if (!isRec) return;
      const keep = out[at.get(code)];
      if (typeof keep !== 'object') return;
      (item.evidenceAll || []).concat(line ? [line] : []).forEach(e => {
        if (!keep.evidenceAll.some(k => k.evidence === e.evidence)) keep.evidenceAll.push(e);
      });
      ['score', 'siteScore', 'policyScore', 'siteLoc', 'policyLoc', 'confidence'].forEach(k => {
        if (typeof item[k] === 'number') keep[k] = Math.max(keep[k] || 0, item[k]);
      });
    });
    return out;
  }

  root.spJurisdictionCode = jurisdictionCode;
  root.spIsDCCityAfter = isDCCityAfter;
  root.spCanonDCText = canonDCText;
  root.spOneRecordPerJurisdiction = oneRecordPerJurisdiction;
  root.SP_DC_DISPLAY = DC_DISPLAY;
  root.spZipState = zipState;
  root.spVerifyLocations = verifyLocations;
  root.spMergeLocationStates = mergeLocationStates;
  root.SP_OPERATING_ROLES = OPERATING_ROLES;
})(typeof window !== 'undefined' ? window : globalThis);
